import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Loader2, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RegionSelect from '@/components/common/RegionSelect';
import { useCurrencyRegions } from '@/hooks/useCurrencyRegions';
import { defaultRegion, regionBySlug } from '@/lib/regions';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import {
  EnterprisePlanRow,
  licenseError,
  QK_LICENSING,
  useLicensePrices,
  useLicensing,
} from '@/hooks/useLicensing';
import { hetznerCost, priceInForce, CatalogData, ServerConfig, today } from '@/lib/pricing';
import SetPriceDialog, { PriceTarget } from './SetPriceDialog';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function PlanLicenceFee({ plan }: { plan: EnterprisePlanRow }) {
  const { add, remove } = useLicensePrices();
  const [amount, setAmount] = React.useState('');
  const [date, setDate] = React.useState(today());
  // Not a choice: a plan is priced in its own region, and the server refuses
  // anything else.
  const region = plan.region_slug;

  return (
    <div className="mt-3 rounded-md border bg-muted/30 p-3" data-testid={`plan-license-${plan.slug}`}>
      <p className="text-xs font-medium">Licence fee for servers they run themselves</p>
      {plan.license_prices.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Nothing agreed, so they pay their tier's fee.
        </p>
      ) : (
        <table className="mt-2 w-full text-xs">
          <tbody>
            {plan.license_prices.map((row) => (
              <tr key={row.id} className="border-t" data-testid={`plan-license-row-${row.id}`}>
                <td className="py-1">{day(row.effective_date)}</td>
                <td className="py-1">{Number(row.monthly_price).toLocaleString()} {row.currency}</td>
                <td className="py-1 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(row.id)} title="Remove">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="w-28 space-y-1">
          <Label className="text-xs">Monthly fee ({plan.region_currency})</Label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid={`plan-license-price-${plan.slug}`} />
        </div>
        <div className="w-40 space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid={`plan-license-date-${plan.slug}`} />
        </div>
        <Button
          size="sm"
          onClick={() => add.mutate(
            { plan: plan.id, monthly_price: amount, region, effective_date: date, notes: '' },
            { onSuccess: () => setAmount('') },
          )}
          disabled={add.isPending || !amount}
          data-testid={`plan-license-set-${plan.slug}`}
        >
          Set
        </Button>
      </div>
    </div>
  );
}

function PlanCloudPrices({
  plan,
  configs,
  serverTypes,
  onSet,
  region,
}: {
  plan: EnterprisePlanRow;
  configs: ServerConfig[];
  serverTypes: Map<string, CatalogData['server_types'][number]>;
  onSet: (target: PriceTarget) => void;
  region: string;
}) {
  if (configs.length === 0) return null;
  return (
    <div className="mt-3 rounded-md border bg-muted/30 p-3" data-testid={`plan-prices-${plan.slug}`}>
      <p className="text-xs font-medium">Cloud server prices</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Anything left alone is charged at their tier's price. Click a size to agree a different one.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {configs.map((config) => {
          // One region, chosen above: picking whichever had a price first
          // showed an arbitrary one once two regions shared a currency.
          const agreed = priceInForce(config.prices[plan.id]?.[region]);
          const cost = hetznerCost(serverTypes.get(config.hetzner_server_type), config.hetzner_location);
          return (
            <button
              key={config.id}
              type="button"
              className={`rounded-md border px-2.5 py-1.5 text-left text-xs hover:bg-muted ${agreed ? 'border-primary/60' : ''}`}
              data-testid={`plan-price-${plan.slug}-${config.hetzner_server_type}`}
              onClick={() => onSet({
                configId: config.id,
                configName: config.display_name,
                scope: { plan: plan.id },
                scopeKey: plan.id,
                scopeLabel: plan.name,
                prices: config.prices,
                cost,
              })}
            >
              <span className="font-medium">{config.display_name}</span>
              <span className="ml-2 font-mono">
                {agreed
                  ? `${Number(agreed.monthly_price).toLocaleString()} ${agreed.currency}`
                  : <span className="text-muted-foreground">inherits</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EnterprisePlansCard() {
  const queryClient = useQueryClient();
  const license = useLicensing();
  const cardRegions = useCurrencyRegions();
  const [cardRegion, setCardRegion] = React.useState('');
  const [target, setTarget] = React.useState<PriceTarget | null>(null);

  React.useEffect(() => {
    if (cardRegion) return;
    const start = defaultRegion(cardRegions.data?.regions ?? []);
    if (start) setCardRegion(start.slug);
  }, [cardRegion, cardRegions.data]);

  // A plan belongs to one region, so the selector picks which plans exist
  // here, not merely which prices to show.
  const plansHere = (license.data?.plans ?? []).filter(
    (plan) => !cardRegion || plan.region_slug === cardRegion,
  );

  const [planName, setPlanName] = React.useState('');
  const [planSlug, setPlanSlug] = React.useState('');
  const [planDescription, setPlanDescription] = React.useState('');
  const [planFeatures, setPlanFeatures] = React.useState<string[]>([]);

  const configs = useQuery<ServerConfig[]>({
    queryKey: ['hetzner-configs'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-configs/')).data;
    },
  });

  const catalog = useQuery<CatalogData>({
    queryKey: ['hetzner-catalog'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-catalog/')).data;
    },
  });

  const serverTypes = React.useMemo(() => {
    const map = new Map<string, CatalogData['server_types'][number]>();
    catalog.data?.server_types.forEach((type) => map.set(type.name, type));
    return map;
  }, [catalog.data]);

  const offered = React.useMemo(
    () => (configs.data ?? []).filter((config) => config.is_active)
      .sort((a, b) => a.sort_order - b.sort_order),
    [configs.data],
  );

  const licenseChanged = () => queryClient.invalidateQueries({ queryKey: QK_LICENSING });

  const addPlan = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/enterprise-plans/', {
        name: planName,
        slug: planSlug || planName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        description: planDescription,
        features: planFeatures,
        region: cardRegion,
      });
    },
    onSuccess: () => {
      licenseChanged();
      setPlanName(''); setPlanSlug(''); setPlanDescription(''); setPlanFeatures([]);
      toast.success('Enterprise plan added.');
    },
    onError: licenseError,
  });

  const patchPlan = useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<EnterprisePlanRow>) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/global-admin/enterprise-plans/${id}/`, patch);
    },
    onSuccess: licenseChanged,
    onError: licenseError,
  });

  const removePlan = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/enterprise-plans/${id}/`);
    },
    onSuccess: () => { licenseChanged(); toast.success('Plan removed.'); },
    onError: licenseError,
  });

  const liveTarget = React.useMemo(() => {
    if (!target) return null;
    const config = offered.find((row) => row.id === target.configId);
    return config ? { ...target, prices: config.prices } : target;
  }, [target, offered]);

  return (
    <Card data-testid="enterprise-plans-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4" />
              Enterprise plans
            </CardTitle>
          </div>
          <div className="w-64">
            <RegionSelect
              value={cardRegion}
              onChange={setCardRegion}
              regions={cardRegions.data?.regions ?? []}
              testId="enterprise-plans-region"
            />
          </div>
        </div>
        <CardDescription>
          What a negotiated customer pays and what their Bailey servers can do.
          A plan prices only what was agreed; everything else falls back to the
          tier's price, and then to small business. Features are available on
          this tier alone. Assign a plan to a customer on the Customers page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {license.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <div className="space-y-3" data-testid="enterprise-plan-list">
              {plansHere.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {(license.data?.plans ?? []).length === 0
                    ? "No plans yet. An enterprise customer without one pays the enterprise tier's prices and has no extra features."
                    : `No plans in ${regionBySlug(cardRegions.data?.regions ?? [], cardRegion)?.name ?? 'this region'} yet. A plan belongs to one region, so a customer billed here needs one of their own.`}
                </p>
              )}
              {plansHere.map((plan) => (
                <div key={plan.id} className={`rounded-md border p-3 ${plan.is_active ? '' : 'opacity-60'}`} data-testid={`enterprise-plan-${plan.slug}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-[20rem] flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{plan.slug}</span>
                        {!plan.is_active && <Badge variant="secondary">inactive</Badge>}
                      </div>
                      {plan.description && <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(license.data?.features ?? []).map((feature) => {
                          const on = plan.features.includes(feature.slug);
                          return (
                            <button
                              key={feature.slug}
                              type="button"
                              title={feature.description}
                              onClick={() => patchPlan.mutate({ id: plan.id, features: on ? plan.features.filter((f) => f !== feature.slug) : [...plan.features, feature.slug] })}
                              className={`rounded-full border px-2.5 py-0.5 text-xs ${on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                              data-testid={`enterprise-plan-${plan.slug}-feature-${feature.slug}`}
                              aria-pressed={on}
                            >
                              {feature.name}
                            </button>
                          );
                        })}
                      </div>
                      <PlanCloudPrices plan={plan} configs={offered} serverTypes={serverTypes} onSet={setTarget} region={cardRegion} />
                      <PlanLicenceFee plan={plan} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="mr-2 text-xs text-muted-foreground">{plan.organizations} on this plan</span>
                      <Button size="sm" variant="ghost" onClick={() => patchPlan.mutate({ id: plan.id, is_active: !plan.is_active })} data-testid={`enterprise-plan-${plan.slug}-toggle`}>
                        {plan.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removePlan.mutate(plan.id)} disabled={plan.organizations > 0} title={plan.organizations > 0 ? 'Organisations are on this plan' : 'Remove'}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 border-t pt-4">
              <p className="text-sm font-medium">Add a plan</p>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="enterprise-plan-name">Name</Label>
                  <Input id="enterprise-plan-name" value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="Acme 2026" data-testid="enterprise-plan-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enterprise-plan-slug">Identifier</Label>
                  <Input id="enterprise-plan-slug" value={planSlug} onChange={(e) => setPlanSlug(e.target.value)} placeholder="from the name" data-testid="enterprise-plan-slug" />
                </div>
                <div className="space-y-1.5">
                  <Label>Region</Label>
                  <RegionSelect
                    value={cardRegion}
                    onChange={setCardRegion}
                    regions={cardRegions.data?.regions ?? []}
                    testId="enterprise-plan-region"
                  />
                  <p className="text-xs text-muted-foreground">
                    A plan is priced in one region and can only be sent to customers billed
                    there.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enterprise-plan-description">What was agreed</Label>
                  <Input id="enterprise-plan-description" value={planDescription} onChange={(e) => setPlanDescription(e.target.value)} data-testid="enterprise-plan-description" />
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(license.data?.features ?? []).map((feature) => {
                  const on = planFeatures.includes(feature.slug);
                  return (
                    <button
                      key={feature.slug}
                      type="button"
                      title={feature.description}
                      onClick={() => setPlanFeatures(on ? planFeatures.filter((f) => f !== feature.slug) : [...planFeatures, feature.slug])}
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                      data-testid={`enterprise-plan-feature-${feature.slug}`}
                      aria-pressed={on}
                    >
                      {feature.name}
                    </button>
                  );
                })}
              </div>
              <Button onClick={() => addPlan.mutate()} disabled={addPlan.isPending || !planName.trim()} data-testid="enterprise-plan-save">
                {addPlan.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Add plan
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <SetPriceDialog target={liveTarget} defaultRegion={cardRegion} onClose={() => setTarget(null)} />
    </Card>
  );
}
